import Popover from '@material-ui/core/Popover'
import Tooltip from '@material-ui/core/Tooltip'
import CloseOutlined from '@material-ui/icons/CloseOutlined'
import MailOutlined from '@material-ui/icons/MailOutlined'
import PeopleOutlined from '@material-ui/icons/PeopleOutlined'
import {useMemo, useRef, useState} from 'react'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import messages from '../../../assets/messages'
import {PammOfferType} from '../../../graphql/types'
import {formatCreated, formatFunds, formatShare, PLACEHOLDER, toInvestorBook} from '../utils/investorBook'
import {formatKpiMoney} from '../utils/kpiGrid'
import {DEFAULT_SORT, filterInvestors, SEARCH_THRESHOLD, SortKey, sortInvestors} from '../utils/investorTable'
import {
  ClearButton,
  CountButton,
  CreatedCell,
  EmailLink,
  EmptyState,
  FundsCell,
  HeadRow,
  Header,
  HeaderAum,
  HeaderCaption,
  HeaderTitle,
  MaskedEmailText,
  NameCell,
  NameText,
  PopoverBody,
  Row,
  ScrollArea,
  SearchBar,
  SearchCount,
  SearchInput,
  SortArrow,
  SortButton,
  Table,
  Td,
  Th,
  TotalLabel,
  TotalRow,
} from './InvestorListPopover.styled'

type InvestorListPopoverProps = {
  offer: PammOfferType
} & WithNamespaces

/**
 * The investor-count meta control on an offer card (PT-5705). Clicking it opens a popover,
 * anchored to the card, listing every investor that funds the node, with a Funds/Share totals
 * row. The rows come from the server (`offer.investors`), which returns them only for nodes the
 * viewer owns — hiding this trigger is cosmetic, that check is the control (PT-5708). The table
 * applies {@link DEFAULT_SORT} (funds descending) initially, and clicking a column header
 * re-sorts by that column, toggling direction on repeat clicks (PT-5707). The sort resets to
 * the default when the popover closes. Esc, an outside click or a re-click close the popover
 * and return focus to the trigger (handled by MUI's Modal/Popover).
 *
 * @param offer - The PAMM offer whose investor book is shown; supplies the rows, count and AUM.
 * @param t - Translation function injected by `withNamespaces` (aria-label).
 * @param i18n - i18next instance injected by `withNamespaces`; its language drives number/date locale.
 */
const _InvestorListPopover = ({offer, t, i18n}: InvestorListPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const count = offer.totalInvestments ?? 0
  // The node's current AUM: the Funds column totals to it and the Share column is a percentage of it
  // (both computed server-side from the same rows). Coerced to 0 only for the share-total guard
  // below — the header renders the raw nullable AUM so a missing value shows "–" (never "$0"),
  // matching the card and the KPI panel's Total Funds tile exactly (PT-5715).
  const aum = offer.aum ?? 0
  const currency = offer.currency ?? 'USD'
  const locale = i18n?.language
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [query, setQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  // `null`/absent means the server would not give us the book — the viewer does not own this node
  // (PT-5708), or the read failed. Either way there is no book to open, and it must not be
  // confused with an offer that genuinely has no investors (an empty list).
  const isBookAvailable = offer.investors != null

  // The full book, unsorted — Share % and the totals row are always computed from THIS list,
  // never from a sorted/filtered view (AC: shares never re-normalise). The server returns rows
  // only for nodes this viewer owns, so there is nothing to gate here (PT-5708).
  const investors = useMemo(() => toInvestorBook(offer.investors), [offer.investors])

  // Filter and sort are separate memos: changing the sort must not redo the filter.
  // No debounce — the filter is a pure pass over <=500 in-memory rows, and the AC budgets
  // 100 ms for results; a debounce could only add latency here.
  const filtered = useMemo(() => filterInvestors(investors, query), [investors, query])
  const rows = useMemo(() => sortInvestors(filtered, sort.key, sort.dir), [filtered, sort])

  const showSearch = investors.length > SEARCH_THRESHOLD
  const isFiltering = query.trim().length > 0

  // Clicking the active column flips direction; a new column starts ascending.
  const toggleSort = (key: SortKey) =>
    setSort((current) => ({key, dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc'}))

  const closePopover = () => setAnchorEl(null)

  // AC: sort resets on close. The query resets too — a stale filter would reopen the popover on
  // a near-empty table with no visible cause, which reads as a bug. Reset in `onExited`, not
  // synchronously in `closePopover`: MUI keeps the popover's children mounted throughout the
  // Grow exit transition (~195ms), so resetting eagerly would re-render the still-visible
  // popover back to the full, re-sorted list while it fades out.
  const resetOnExited = () => {
    setSort(DEFAULT_SORT)
    setQuery('')
  }

  const hasInvestors = investors.length > 0

  const trigger = (
    <CountButton
      type="button"
      disabled={!hasInvestors}
      onClick={(event) => setAnchorEl(event.currentTarget)}
      aria-label={t(messages.pammInvestorBookAria.i18nKey, messages.pammInvestorBookAria.defaults)}
      aria-haspopup="dialog"
      aria-expanded={Boolean(anchorEl)}
    >
      <PeopleOutlined style={{fontSize: 14}} />
      {/* `investorsCount` (not `count`): `count` is an i18next reserved keyword that triggers
          plural-key resolution and leaves the number blank. */}
      <Trans {...messages.pammInvestorsCount} values={{investorsCount: count}} />
    </CountButton>
  )

  // Not our node (or the book failed to load) → no trigger at all. This is the AC's "the client
  // hides the popover trigger on non-owned nodes", and it is cosmetic by design: the server has
  // already refused the data, which is the actual control. Rendering a disabled "no investors"
  // trigger here would instead assert something false about someone else's offer.
  if (!isBookAvailable) return null

  // No investors → the disabled count explains itself via a tooltip rather than opening an empty
  // popover. A disabled button swallows pointer events, so wrap it in a focusable <span> that the
  // Tooltip can anchor to (MUI requirement).
  if (!hasInvestors) {
    return (
      <Tooltip title={t(messages.pammInvestorBookNoInvestors.i18nKey, messages.pammInvestorBookNoInvestors.defaults)}>
        <span style={{display: 'inline-flex'}}>{trigger}</span>
      </Tooltip>
    )
  }

  const totalFunds = investors.reduce((sum, investor) => sum + investor.funds, 0)
  // Shares are only defined when there is AUM to split; with zero AUM every share is null and the
  // total is undefined too (rendered "–") — never a bogus 0.00%.
  const totalShare = aum > 0 ? investors.reduce((sum, investor) => sum + (investor.share ?? 0), 0) : null

  const ariaSort = (key: SortKey): 'ascending' | 'descending' | undefined =>
    sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined

  const sortableHeader = (key: SortKey, message: {i18nKey: string; defaults: string}, align?: 'right') => (
    <Th align={align} aria-sort={ariaSort(key)}>
      <SortButton
        type="button"
        onClick={() => toggleSort(key)}
        aria-label={t(messages.pammInvestorBookSortAria.i18nKey, messages.pammInvestorBookSortAria.defaults, {
          column: t(message.i18nKey, message.defaults),
        })}
      >
        <Trans {...message} />
        <SortArrow active={sort.key === key} arrowDir={sort.dir} />
      </SortButton>
    </Th>
  )

  return (
    <>
      {trigger}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        TransitionProps={{onExited: resetOnExited}}
        PaperProps={{
          role: 'dialog',
          'aria-label': t(messages.pammInvestorBookTitle.i18nKey, messages.pammInvestorBookTitle.defaults),
          style: {overflow: 'hidden', borderRadius: 12},
        }}
      >
        <PopoverBody>
          <Header>
            <div>
              <HeaderCaption>
                <Trans {...messages.pammInvestorBookTitle} />
              </HeaderCaption>
              <HeaderTitle>
                <Trans
                  {...messages.pammInvestorBookSubtitle}
                  values={{owner: offer.name, investorsTotal: investors.length}}
                />
              </HeaderTitle>
            </div>
            <div>
              <HeaderCaption>
                <Trans {...messages.pammInvestorBookAum} />
              </HeaderCaption>
              <HeaderAum>{formatKpiMoney(offer.aum, currency)}</HeaderAum>
            </div>
          </Header>

          {showSearch && (
            <SearchBar>
              <SearchInput
                ref={searchInputRef}
                type="text"
                value={query}
                autoFocus
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t(messages.pammInvestorBookSearch.i18nKey, messages.pammInvestorBookSearch.defaults)}
                aria-label={t(messages.pammInvestorBookSearch.i18nKey, messages.pammInvestorBookSearch.defaults)}
              />
              {/* Always mounted (not just while filtering): a screen reader only announces
                  changes to a live region that already existed in the DOM, so the region has to
                  be present — empty — before the first keystroke, or "N of M" is never spoken. */}
              <SearchCount role="status" aria-live="polite">
                {isFiltering && (
                  <Trans
                    {...messages.pammInvestorBookSearchCount}
                    values={{shown: rows.length, total: investors.length}}
                  />
                )}
              </SearchCount>
              {isFiltering && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    // Clearing unmounts this button while it holds focus, which would otherwise
                    // drop focus to <body>. Refocus the search input it sits next to instead.
                    setQuery('')
                    searchInputRef.current?.focus()
                  }}
                  aria-label={t(
                    messages.pammInvestorBookSearchClear.i18nKey,
                    messages.pammInvestorBookSearchClear.defaults,
                  )}
                >
                  <CloseOutlined style={{fontSize: 14}} />
                </ClearButton>
              )}
            </SearchBar>
          )}

          {rows.length === 0 ? (
            <EmptyState>
              <Trans {...messages.pammInvestorBookSearchEmpty} />
            </EmptyState>
          ) : (
            <ScrollArea>
              <Table>
                <thead>
                  <HeadRow>
                    {sortableHeader('name', messages.pammInvestorBookColInvestor)}
                    {sortableHeader('funds', messages.pammInvestorBookColFunds, 'right')}
                    {sortableHeader('share', messages.pammInvestorBookColShare, 'right')}
                    {sortableHeader('created', messages.pammInvestorBookColCreated)}
                    {/* Email is not sortable — the AC lists Investor, Funds, Share % and Created. */}
                    <Th>
                      <Trans {...messages.pammInvestorBookColEmail} />
                    </Th>
                  </HeadRow>
                </thead>
                <tbody>
                  {rows.map((investor) => (
                    <Row key={investor.id}>
                      <NameCell>
                        <NameText title={investor.name}>{investor.name}</NameText>
                      </NameCell>
                      <FundsCell align="right">{formatFunds(investor.funds, currency, locale)}</FundsCell>
                      <Td align="right">{formatShare(investor.share)}</Td>
                      <CreatedCell>{formatCreated(investor.created, locale)}</CreatedCell>
                      <Td>
                        {/*
                          A missing address renders as "–" rather than a dead `mailto:` link, and a
                          masked one (`j***@domain`, served to upstream agents who do not own this
                          node) renders as plain text for the same reason — it is not a real
                          address to write to (PT-5709).
                        */}
                        {!investor.email ? (
                          PLACEHOLDER
                        ) : investor.emailMasked ? (
                          <MaskedEmailText
                            title={t(
                              messages.pammInvestorBookEmailMasked.i18nKey,
                              messages.pammInvestorBookEmailMasked.defaults,
                            )}
                          >
                            {investor.email}
                          </MaskedEmailText>
                        ) : (
                          <EmailLink href={`mailto:${investor.email}`}>
                            <MailOutlined style={{fontSize: 13}} />
                            {investor.email}
                          </EmailLink>
                        )}
                      </Td>
                    </Row>
                  ))}
                </tbody>
                <tfoot>
                  <TotalRow>
                    <TotalLabel>
                      <Trans {...messages.pammInvestorBookTotal} />
                    </TotalLabel>
                    <td style={{textAlign: 'right'}}>{formatFunds(totalFunds, currency, locale)}</td>
                    <td style={{textAlign: 'right'}}>{formatShare(totalShare)}</td>
                    <td colSpan={2} />
                  </TotalRow>
                </tfoot>
              </Table>
            </ScrollArea>
          )}
        </PopoverBody>
      </Popover>
    </>
  )
}

export const InvestorListPopover = withNamespaces()(_InvestorListPopover)
